import React from 'react';
// Importez Autocomplete et les autres composants nécessaires
import { Autocomplete, TextField, FormControl, InputLabel } from '@mui/material';

// Le composant Autocomplete est beaucoup plus flexible que Select pour la recherche.
// Il gère la recherche, le focus, et l'affichage de la liste pour vous.
export default function SimpleSelect({ value, onChange, label, required, multiple, options, color, sx }) {
    // Note: Pour Autocomplete, la prop 'options' doit être un tableau d'objets, 
    // et chaque objet doit avoir une propriété que l'on peut identifier (comme 'label').
    // Si vos 'options' sont déjà au format { value: ..., label: ... }, cela fonctionnera.

    // Gère le changement de valeur pour s'assurer que le format correspond à ce que 
    // le composant parent attend, si nécessaire.
    const handleChange = (event, newValue) => {
        // Dans le cas de l'Autocomplete, 'newValue' est soit l'objet sélectionné (single)
        // soit un tableau d'objets sélectionnés (multiple).
        
        // Si vous avez besoin de retourner seulement les 'value' comme dans votre Select original,
        // vous devrez mapper 'newValue' :
        if (multiple) {
            const values = newValue ? newValue.map(option => option.value) : [];
            onChange({ target: { value: values } });
        } else {
            const value = newValue ? newValue.value : null;
            onChange({ target: { value: value } });
        }
    };
    
    // Pour l'Autocomplete, 'value' doit être l'objet complet ou un tableau d'objets.
    // Nous devons trouver les objets correspondants aux 'value' (qui sont les 'value' simples)
    const autocompleteValue = React.useMemo(() => {
        if (multiple) {
            // value est un tableau de IDs/valeurs, on cherche les objets correspondants
            if (Array.isArray(value)) {
                return options.filter(option => value.includes(option.value));
            }
            return [];
        } else {
            // value est un ID/valeur simple, on cherche l'objet correspondant
            return options.find(option => option.value === value) || null;
        }
    }, [value, options, multiple]);


    return (
        // Note: Avec Autocomplete, vous n'avez généralement pas besoin de FormControl et InputLabel séparés,
        // car le TextField à l'intérieur de l'Autocomplete gère le label.
        <Autocomplete
            // Vous pouvez utiliser 'disablePortal' si vous souhaitez que le menu reste dans 
            // le flux de la page et non dans le corps du document.
            // disablePortal
            
            // La valeur actuelle (doit être un objet ou un tableau d'objets)
            value={autocompleteValue}
            
            // Le tableau complet des options
            options={options}
            
            // Gère le cas où l'utilisateur tape une valeur qui n'est pas dans la liste.
            // set à false pour restreindre aux options données.
            freeSolo={false}
            
            // La propriété pour identifier l'option affichée dans la liste
            getOptionLabel={(option) => option.label || ''}
            
            // La propriété pour tester l'égalité entre deux options
            isOptionEqualToValue={(option, val) => option.value === val.value}
            
            // Gestionnaire de changement
            onChange={handleChange}

            // Gestion des sélections multiples
            multiple={multiple}

            // Styles
            sx={sx}
            
            // Le composant d'entrée (le champ de texte) qui permet la recherche
            renderInput={(params) => (
                <TextField 
                    {...params} 
                    label={label}
                    required={required}
                    color={color}
                    focused={color === "warning"}
                    // Cette prop est importante pour s'assurer que le label se comporte correctement
                    InputLabelProps={{ shrink: true }}
                />
            )}
        />
    );
}

// Exemple de format d'options attendu:
/*
const myOptions = [
    { label: 'Option A', value: 1 },
    { label: 'Option B', value: 2 },
    { label: 'Option C', value: 3 },
];
*/